'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submissionService } from '@/lib/services/submissionService';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await submissionService.createContactSubmission(formData);
      
      if (result) {
        setSubmitMessage('Thank you for contacting us! We will get back to you within 24 hours.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
      <h3 className="font-display text-3xl uppercase tracking-tight text-foreground mb-6">
        Send us a Message
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="contact_name" className="block text-sm font-semibold text-foreground mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="contact_name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            placeholder="Enter your full name"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="contact_email" className="block text-sm font-semibold text-foreground mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="contact_email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            placeholder="Enter your email address"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="contact_phone" className="block text-sm font-semibold text-foreground mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="contact_phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            pattern="[0-9]{10}"
            className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            placeholder="Enter 10-digit phone number"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="contact_message" className="block text-sm font-semibold text-foreground mb-2">
            Message *
          </label>
          <textarea
            id="contact_message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={5}
            className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none"
            placeholder="Tell us how we can help you..."
          ></textarea>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Send Message
              <Icon name="PaperAirplaneIcon" size={20} />
            </>
          )}
        </button>

        {/* Success Message */}
        {submitMessage && (
          <div className="bg-success/10 border border-success/20 rounded-xl p-4 text-center animate-fadeIn">
            <p className="text-success font-medium">{submitMessage}</p>
          </div>
        )}
      </form>
    </div>
  );
}